import React, {
  ChangeEvent,
  useCallback,
  useEffect,
  useMemo,
  useState
} from 'react'
import { useDispatch } from 'react-redux'

import { useQuery } from '@tanstack/react-query'
import { Alert } from '@vtex/shoreline'
import { t } from 'i18next'

import { Button } from '~/components/Buttons'
import Table from '~/components/Table'
import { Badge } from '~/components/UI/Badge'
import { FileInput, FormGroup, InputHidden, Label } from '~/components/unform'
import * as PIDS from '~/modules/retailMedia/components/CampaignForm/hooks/constants/publisherIds'
import { listSizesBySegmentationService } from '~/modules/retailMedia/services/placements/sizes'
import { getPublisherConfiguration } from '~/modules/retailMedia/store/industry/publisherConfiguration/actions'

import FileToUpload from '../FileToUpload'
import { getDimensions } from './helpers'
import { schema } from './schema'
import { schemaVideo } from './schemaVideo'

import * as S from './styles'

const mediaTypes = {
  'image/png': 'image',
  'image/jpeg': 'image',
  'video/mp4': 'video'
}

export const FieldsetAssets = ({
  initialData,
  handleIsValid,
  type = 'image'
}: Pick<
  SponsoredBrandFieldsetProps,
  'initialData' | 'handleIsValid' | 'type'
>) => {
  const [assetsReady, setAssetsReady] = useState<AssetsReady[]>([])

  // Agrupar assets por dimensão
  type AssetByDimension = {
    dimension: string
    assets: AssetsReady[]
  }[]

  const assetsGroupedByDimension: AssetByDimension = useMemo(() => {
    return assetsReady.reduce((acc: AssetByDimension, asset) => {
      const dimension = `${asset.dimensions.width}x${asset.dimensions.height}`
      const existingGroup = acc.find(group => group.dimension === dimension)

      if (existingGroup) {
        existingGroup.assets.push(asset)
      } else {
        acc.push({ dimension, assets: [asset] })
      }

      return acc
    }, [])
  }, [assetsReady])

  /**
   * Lógica de apoio
   */
  const { publisherId } = useMemo(
    () => ({
      publisherId: initialData?.publisher_id,
      publisherName: initialData?.publisher_name,
      settings: initialData?.settings
    }),
    [initialData]
  )

  // Sponsored Brand without banner is only supported for Fastshop.
  const isSponsoredBrandWithoutBanner = useMemo(() => {
    switch (publisherId) {
      case PIDS.FASTSHOP:
        return true
      default:
        return false
    }
  }, [publisherId])

  useEffect(() => {
    handleIsValid({
      key: 'assets',
      value: assetsReady?.length > 0 || isSponsoredBrandWithoutBanner
    })
  }, [assetsReady?.length, handleIsValid, isSponsoredBrandWithoutBanner])

  /**
   * Lógica para a funcionalidade principal
   */
  type FilesToUpload = { file: File; id: string }[]
  const [filesToUpload, setFilesToUpload] = useState<FilesToUpload>([])

  const dispatch = useDispatch()

  useEffect(() => {
    if (publisherId) {
      dispatch(getPublisherConfiguration(publisherId))
    }
  }, [dispatch, publisherId])

  const handleDeleteAsset = useCallback(
    (data: { id: string }) => {
      setAssetsReady(state => state.filter(item => item.id !== data.id))
    },
    [setAssetsReady]
  )

  const handleSaveBanner = useCallback(
    (data: ItemAssetTable) => {
      setFilesToUpload(prevState =>
        prevState.filter(item => item.id !== data.id)
      )

      setAssetsReady(prevState => {
        return [
          ...prevState,
          {
            ...data,
            isNew: true,
            type:
              (mediaTypes?.[data.fileMedia.type as keyof typeof mediaTypes] as
                | 'image'
                | 'video') || data.fileMedia.type,
            handleDeleteAsset
          }
        ]
      })
    },
    [handleDeleteAsset]
  )

  /**
   * Handle multiple upload
   */

  const handlePrepareToUploadImage = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const fileList = Array.from(
        event.target.files as FileList,
        (item, index) => ({ file: item, id: `${item.name}_${index}` })
      )

      setFilesToUpload(fileList)
    },
    []
  )

  const handleDeleteFileBeforeUpload = (removeId: string) => {
    setFilesToUpload(prevState =>
      prevState.filter(item => item.id !== removeId)
    )
  }

  const placementType =
    type === 'video' ? 'sponsored_brand_video' : 'sponsored_brand'

  const {
    data: dataAdSize,
    isError: catalogHasError,
    refetch: refetchCatalogAdSizes
  } = useQuery({
    queryKey: ['get-sponsored-brand-ad-sizes', placementType, publisherId],
    queryFn: async () => {
      const res = await listSizesBySegmentationService({
        publisher_id: publisherId,
        type: placementType
      })
      return res.data ?? []
    },
    enabled: !!publisherId && !isSponsoredBrandWithoutBanner
  })

  const catalogIsEmpty = !catalogHasError && dataAdSize?.length === 0
  const catalogHasSizes = !catalogHasError && !!dataAdSize?.length

  /**
   * Placements
   */

  const options = useMemo(() => {
    if (dataAdSize) {
      const arrayValue: OptionsInterface[] = dataAdSize.map(item => ({
        value: item.name,
        label: item.size,
        width: item.width,
        height: item.height
      }))

      arrayValue.sort((a, b) =>
        a.width !== b.width ? a.width - b.width : a.height - b.height
      )

      return arrayValue
    }
  }, [dataAdSize])

  const getPlacementByDimension = useCallback(
    ({ width, height }: Dimension) => {
      const findPlacement = dataAdSize?.find(
        item => Number(item.width) === width && Number(item.height) === height
      )

      return findPlacement
    },
    [dataAdSize]
  )

  /**
   * Content data
   */

  const label = useMemo(
    () =>
      type === 'video'
        ? t('rm:campaign.create.SponsoredBrandVideo.FieldsetAssets.label')
        : t('rm:campaign.create.SponsoredBrand.FieldsetAssets.label'),
    [type]
  )

  const formatAssets = useCallback(
    (assets: AssetSponsoredBrand[]) =>
      assets.map(asset => ({
        id: asset.url,
        media_url: asset.url,
        fileMedia: new File([], asset.url),
        ad_size_name: asset.dimension || 'default',
        ad_size: asset.dimension || 'default',
        dimensions: getDimensions(asset.dimension),
        type: asset.type,
        handleDeleteAsset: handleDeleteAsset
      })),
    [handleDeleteAsset]
  )

  // Check if has media on edit
  useEffect(() => {
    if (initialData?.settings?.assets?.length) {
      setAssetsReady(formatAssets(initialData.settings.assets))
    }
  }, [formatAssets, initialData?.settings?.assets])

  /**
   * Fastshop layout only supports sponsored brand with text.
   */

  if (isSponsoredBrandWithoutBanner) {
    return null
  }

  let labelHelperText = null
  if (type !== 'video' && catalogHasSizes) {
    labelHelperText = t(
      'rm:campaign.create.SponsoredBrand.FieldsetAssets.helperText',
      { size: dataAdSize?.[0]?.size }
    )
  }

  let catalogSection = null
  if (catalogHasError) {
    catalogSection = (
      <Alert variant="critical">
        {t('rm:campaign.create.SponsoredBrand.FieldsetAssets.error.message')}
        <Button
          onClick={() => refetchCatalogAdSizes()}
          text={t('c:actions.tryAgain')}
        />
      </Alert>
    )
  } else if (catalogIsEmpty) {
    catalogSection = (
      <Alert variant="warning">
        {t('rm:campaign.create.SponsoredBrand.FieldsetAssets.empty.message')}
      </Alert>
    )
  } else if (catalogHasSizes) {
    catalogSection = (
      <>
        <FileInput
          name="mediaFile"
          accept={type === 'video' ? 'video/mp4' : 'image/*'}
          onChange={(event: ChangeEvent<HTMLInputElement>) =>
            handlePrepareToUploadImage(event)
          }
          isMultiple={type !== 'video'}
          hideIcons
        >
          <div className="pt-3 mt-3 border-top border-tertiary">
            <h4>{t('rm:allowedMediaSizes')}</h4>

            <div className="d-flex gap 2 justify-content-center flex-wrap">
              {options?.map(item => (
                <div key={item.value}>
                  <Badge template="primary">{item.label}</Badge>
                </div>
              ))}
            </div>
          </div>
        </FileInput>

        <S.UploadQueue>
          {filesToUpload?.map(item => (
            <FileToUpload
              data={item}
              key={item.id}
              getPlacementByDimension={getPlacementByDimension}
              onDelete={() => handleDeleteFileBeforeUpload(item.id)}
              onSave={handleSaveBanner}
            />
          ))}
        </S.UploadQueue>
      </>
    )
  }

  return (
    <FormGroup>
      <Label text={label} isRequired helperText={labelHelperText} />

      <section className="multipleUploadWrapper">{catalogSection}</section>

      {!!assetsReady.length &&
        assetsReady.map((item, index) => {
          return (
            <React.Fragment key={item.id}>
              <InputHidden
                name={`assets[${index}].dimension`}
                value={`${item.dimensions.width}x${item.dimensions.height}`}
              />
              <InputHidden name={`assets[${index}].type`} value={item.type} />
              <InputHidden
                name={`assets[${index}].url`}
                value={item.media_url}
              />
            </React.Fragment>
          )
        })}

      {assetsGroupedByDimension.map(group => (
        <S.WrapperTabler key={group.dimension}>
          <strong>{group.dimension}</strong>
          <Table
            schema={type === 'video' ? schemaVideo : schema}
            data={group.assets}
          />
        </S.WrapperTabler>
      ))}
    </FormGroup>
  )
}
